import { useMemo, useState } from 'react';
import { Segmented, Stat } from '../components/data';
import { ErrorBanner, Field, Loading } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { num, shortDate, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface Assignment {
  resourceId: number;
  resource: string;
  hours: number;
}
interface SchedProject {
  id: number;
  name: string;
  number: string | null;
  company: string | null;
  lead: string | null;
  leadId: number | null;
  status: string;
  type: string | null;
  department: string | null;
  start: string | null;
  end: string | null;
  created: string | null;
  percentComplete: number | null;
  estimatedHours: number | null;
  actualHours: number | null;
  remainingHours: number;
  remainingFrom: 'tasks' | 'estimate' | 'none';
  openTasks: number;
  totalTasks: number;
  unassignedHours: number;
  assignments: Assignment[];
}
interface Body {
  projects: SchedProject[];
  people: { id: number; name: string; active: boolean }[];
  fetchedUtc: string;
}

type Horizon = '8' | '13' | '26';
const HORIZONS: { id: Horizon; label: string }[] = [
  { id: '8', label: '8 weeks' },
  { id: '13', label: 'A quarter' },
  { id: '26', label: 'Half a year' },
];

const WEEK_MS = 7 * 86_400_000;
const dayOf = (iso: string | null) => (iso ? iso.slice(0, 10) : null);

/** The Monday of the week a day falls in, in this browser's calendar. */
function mondayOf(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return toLocalDateString(d);
}
function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toLocalDateString(d);
}
/** Whole weeks from one Monday to another. Both are parsed as UTC, so an hour
 *  lost or gained to daylight saving cannot round the answer off by a week. */
const weeksBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / WEEK_MS);

/* Client or internal, read as the Autotask dashboard reads it: a project type
   that says Internal is internal, everything else is a client's. */
const isInternal = (p: SchedProject) => /internal/i.test(p.type ?? '');

const hrs = (v: number) => num(v, v >= 10 ? 0 : 1);

/**
 * How full a week is, as one of the five heat steps. Over capacity is the
 * top two: a person booked past their week is the thing this page is for.
 */
function loadClass(hours: number, capacity: number): string {
  const r = hours / Math.max(capacity, 1);
  if (r <= 0.5) return 'heat-1';
  if (r <= 0.8) return 'heat-2';
  if (r <= 1) return 'heat-3';
  if (r <= 1.25) return 'heat-4';
  return 'heat-5';
}

interface Plan {
  p: SchedProject;
  /** Every week the work is spread across, whether or not it is on screen. */
  spans: string[];
  /** Its end date has already passed: the work is owed now, not later. */
  late: boolean;
  /** Autotask has no end date, so the finish below is this page's guess. */
  undated: boolean;
}

/**
 * When a project's remaining hours fall.
 *
 * From its start date — or today, if it started already — to its end date,
 * spread evenly across those weeks. A project whose end date has passed is
 * owed now: all of it lands in the current week, where it shows as the
 * overload it is. A project with no end date is run at one person's full
 * capacity until the hours are gone, which is a guess, and said to be one.
 */
function planOf(p: SchedProject, capacity: number, thisWeek: string, today: string): Plan {
  const start = dayOf(p.start) ?? dayOf(p.created);
  const end = dayOf(p.end);
  const begin = start && start > today ? start : today;
  const beginWeek = mondayOf(begin) < thisWeek ? thisWeek : mondayOf(begin);
  const late = Boolean(end && end < today);
  let count: number;
  if (late) count = 1;
  else if (end) count = Math.max(1, weeksBetween(beginWeek, mondayOf(end)) + 1);
  else count = Math.max(1, Math.ceil(p.remainingHours / Math.max(capacity, 1)));
  return {
    p,
    spans: Array.from({ length: count }, (_, i) => addDays(beginWeek, i * 7)),
    late,
    undated: !end,
  };
}

interface LoadRow {
  key: string;
  name: string;
  weeks: Map<string, number>;
  total: number;
  projects: Set<number>;
}

/**
 * Projects › Schedule: the hours still to do on every open Autotask project,
 * laid out from each project's start date across the weeks it has left, and
 * added up per person per week against the capacity you set.
 *
 * Two readings of the same figures. The timeline says when each project runs
 * and how heavy it is; the load grid says who is carrying it, and which weeks
 * are already past what a person can take.
 */
export function ProjectsSchedule() {
  const [horizon, setHorizon] = useState<Horizon>('13');
  const [capacity, setCapacity] = useState(30);
  const [kind, setKind] = useState<'all' | 'client' | 'internal'>('all');
  const [unassigned, setUnassigned] = useState<'lead' | 'apart'>('lead');
  const [person, setPerson] = useState('');

  const data = useHeldResource<Body>('/projects/autotask/schedule');
  const loaded = data.data?.projects ?? [];

  const today = todayLocal();
  const thisWeek = mondayOf(today);
  const weeks = useMemo(
    () => Array.from({ length: Number(horizon) }, (_, i) => addDays(thisWeek, i * 7)),
    [horizon, thisWeek],
  );

  const kindCounts = useMemo(() => {
    const internal = loaded.filter(isInternal).length;
    return { all: loaded.length, client: loaded.length - internal, internal };
  }, [loaded]);

  /* Client or internal is applied first, so every figure on the page is about
     the work that was asked for. Projects with nothing left to do are not
     scheduling, and are left out of all of it. */
  const projects = useMemo(
    () =>
      loaded.filter(
        (p) => p.remainingHours > 0 && (kind === 'all' || (kind === 'internal' ? isInternal(p) : !isInternal(p))),
      ),
    [loaded, kind],
  );

  const plans = useMemo(
    () => projects.map((p) => planOf(p, capacity, thisWeek, today)),
    [projects, capacity, thisWeek, today],
  );

  /* Per person, per week. Hours that fall beyond the horizon are counted
     apart rather than silently dropped, so the page can say they are there. */
  const { rows, beyond, cells } = useMemo(() => {
    const inView = new Set(weeks);
    const map = new Map<string, LoadRow>();
    let past = 0;
    const put = (key: string, name: string, week: string, hours: number, pid: number) => {
      if (!inView.has(week)) {
        past += hours;
        return;
      }
      const row = map.get(key) ?? { key, name, weeks: new Map(), total: 0, projects: new Set<number>() };
      row.weeks.set(week, (row.weeks.get(week) ?? 0) + hours);
      row.total += hours;
      row.projects.add(pid);
      map.set(key, row);
    };
    for (const pl of plans) {
      for (const a of pl.p.assignments) {
        const per = a.hours / pl.spans.length;
        for (const w of pl.spans) put(`r${a.resourceId}`, a.resource, w, per, pl.p.id);
      }
      if (pl.p.unassignedHours > 0) {
        const toLead = unassigned === 'lead' && pl.p.leadId !== null;
        const per = pl.p.unassignedHours / pl.spans.length;
        for (const w of pl.spans) {
          put(toLead ? `r${pl.p.leadId}` : 'unassigned', toLead ? (pl.p.lead ?? 'Unknown') : 'Unassigned', w, per, pl.p.id);
        }
      }
    }
    const list = [...map.values()].sort((a, b) =>
      a.key === 'unassigned' ? 1 : b.key === 'unassigned' ? -1 : b.total - a.total,
    );
    const over = list.reduce((n, r) => n + [...r.weeks.values()].filter((h) => h > capacity).length, 0);
    return { rows: list, beyond: past, cells: over };
  }, [plans, weeks, unassigned, capacity]);

  /* The timeline: every project with at least one week on screen, earliest
     first, narrowed to one person's work when a row is picked. */
  const bars = useMemo(() => {
    const mine = person
      ? plans.filter(
          (pl) =>
            pl.p.assignments.some((a) => `r${a.resourceId}` === person) ||
            (pl.p.unassignedHours > 0 &&
              (unassigned === 'lead' && pl.p.leadId !== null ? `r${pl.p.leadId}` === person : person === 'unassigned')),
        )
      : plans;
    const firstWeek = weeks[0] ?? thisWeek;
    return mine
      .map((pl) => {
        const begins = pl.spans[0] ?? firstWeek;
        const ends = pl.spans[pl.spans.length - 1] ?? begins;
        const first = weeks.indexOf(begins);
        const last = weeks.indexOf(ends);
        const from = first === -1 ? 0 : first;
        const to = last === -1 ? weeks.length - 1 : last;
        /* A project that began before this week still shows, running in from
           the left edge; one that ends after the horizon runs off the right. */
        const onScreen = first !== -1 || Date.parse(ends) >= Date.parse(firstWeek);
        return { pl, from, to, onScreen, runsOn: last === -1 };
      })
      .filter((b) => b.onScreen)
      .sort((a, b) => a.from - b.from || b.pl.p.remainingHours - a.pl.p.remainingHours);
  }, [plans, weeks, person, unassigned]);

  /* A label on the first week and on every week that opens a month: a tick
     every week is unreadable at a quarter, let alone half a year. */
  const ticks = useMemo(
    () =>
      weeks
        .map((w, i) => ({ w, i, newMonth: i === 0 || w.slice(5, 7) !== (weeks[i - 1] ?? '').slice(5, 7) }))
        .filter((t) => t.newMonth || Number(horizon) <= 8),
    [weeks, horizon],
  );

  const booked = rows.reduce((n, r) => n + r.total, 0);
  const remaining = projects.reduce((n, p) => n + p.remainingHours, 0);
  const teamCapacity = rows.filter((r) => r.key !== 'unassigned').length * capacity * weeks.length;
  const needDates = projects.filter((p) => !p.end || (dayOf(p.end) ?? '') < today);
  const guessed = projects.filter((p) => p.remainingFrom === 'estimate').length;
  const picked = rows.find((r) => r.key === person);
  const pct = (n: number) => `${(n * 100).toFixed(0)}%`;
  const weekLabel = (w: string) => `${shortDate(w)}`;

  const exportCsv = () =>
    downloadCsv(`project_schedule_${thisWeek}_${horizon}w_${fileStamp()}.csv`, [
      ['Resource', ...weeks.map((w) => `w/c ${w}`), 'Total', 'Weekly capacity'],
      ...rows.map((r) => [r.name, ...weeks.map((w) => Math.round((r.weeks.get(w) ?? 0) * 10) / 10), Math.round(r.total * 10) / 10, capacity]),
    ]);

  return (
    <div className="mt-4">
      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-4)' }}>
            <Field label="Look ahead">
              <Segmented label="Look ahead" options={HORIZONS} value={horizon} onChange={setHorizon} />
            </Field>
            <Field label="Work">
              <Segmented
                label="Client or internal"
                options={[
                  { id: 'all' as const, label: `All (${kindCounts.all})` },
                  { id: 'client' as const, label: `Client (${kindCounts.client})` },
                  { id: 'internal' as const, label: `Internal (${kindCounts.internal})` },
                ]}
                value={kind}
                onChange={(v) => {
                  setKind(v);
                  setPerson('');
                }}
              />
            </Field>
            <Field label="Project hours a week">
              <input
                className="input"
                type="number"
                min={1}
                max={60}
                step={1}
                style={{ width: 90 }}
                value={capacity}
                onChange={(e) => setCapacity(Math.min(60, Math.max(1, Number(e.target.value) || 1)))}
              />
            </Field>
            <Field label="Hours nobody is on">
              <Segmented
                label="Unassigned hours"
                options={[
                  { id: 'lead' as const, label: 'To the lead' },
                  { id: 'apart' as const, label: 'Kept apart' },
                ]}
                value={unassigned}
                onChange={(v) => {
                  setUnassigned(v);
                  setPerson('');
                }}
              />
            </Field>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={exportCsv}>
              Export CSV
            </button>
          </div>
          <p className="xs muted mt-2">
            Hours still to do on open projects — a task's remaining hours, or the project's estimate less what has been
            worked — spread from each project's start date across the weeks it has left. Capacity is project hours only;
            ticket work is not in these figures.
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && <Loading label="Reading the project schedule from Autotask" />}

      {data.data && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Hours still to do" value={hrs(remaining)} foot={`${num(projects.length)} open projects`} />
            <Stat
              label={`Booked, next ${horizon} weeks`}
              value={hrs(booked)}
              foot={teamCapacity > 0 ? `${pct(booked / teamCapacity)} of ${hrs(teamCapacity)} h capacity` : '—'}
            />
            <Stat
              label="Weeks over capacity"
              value={num(cells)}
              foot={cells === 0 ? 'Nobody is past their week' : `person-weeks above ${capacity} h`}
            />
            <Stat
              label="Need dates"
              value={num(needDates.length)}
              foot="no end date, or already past it"
            />
          </div>

          {projects.length === 0 ? (
            <section className="card">
              <div className="card-body">
                <p className="muted small">No open project has hours left to schedule.</p>
              </div>
            </section>
          ) : (
            <>
              <section className="card mb-4">
                <div className="card-head">
                  <div className="grow">
                    <h3>Who is booked, week by week</h3>
                    <p className="sub">
                      Hours against {capacity} a week. Click a name to see only their projects on the timeline.
                      {unassigned === 'lead'
                        ? ' Hours nobody is assigned to sit with the project lead.'
                        : ' Hours nobody is assigned to are kept in their own row.'}
                    </p>
                  </div>
                  {person && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPerson('')}>
                      Show everyone
                    </button>
                  )}
                </div>
                <div className="table-wrap">
                  <table className="table ps-load">
                    <thead>
                      <tr>
                        <th className="ps-name">Resource</th>
                        {weeks.map((w) => (
                          <th key={w} className="num">
                            {weekLabel(w)}
                          </th>
                        ))}
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.key} className={person === r.key ? 'is-on' : undefined}>
                          <td className="ps-name">
                            <button type="button" className="link-button" onClick={() => setPerson(person === r.key ? '' : r.key)}>
                              {r.name}
                            </button>
                            <div className="xs muted">
                              {num(r.projects.size)} project{r.projects.size === 1 ? '' : 's'}
                            </div>
                          </td>
                          {weeks.map((w) => {
                            const h = r.weeks.get(w) ?? 0;
                            return (
                              <td key={w} className="num">
                                {h > 0 ? <span className={`heat ${loadClass(h, capacity)}`}>{hrs(h)}</span> : <span className="subtle">·</span>}
                              </td>
                            );
                          })}
                          <td className="num">{hrs(r.total)}</td>
                        </tr>
                      ))}
                      <tr className="ps-total">
                        <td className="ps-name">Everyone</td>
                        {weeks.map((w) => {
                          const h = rows.reduce((n, r) => n + (r.weeks.get(w) ?? 0), 0);
                          return (
                            <td key={w} className="num">
                              {hrs(h)}
                            </td>
                          );
                        })}
                        <td className="num">{hrs(booked)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {beyond > 0 && (
                  <div className="card-body">
                    <p className="xs muted">
                      A further {hrs(beyond)} hours fall after these {horizon} weeks.
                    </p>
                  </div>
                )}
              </section>

              <section className="card mb-4">
                <div className="card-head">
                  <div className="grow">
                    <h3>
                      Timeline{picked ? ` — ${picked.name}` : ''}
                    </h3>
                    <p className="sub">
                      Each project from its start date to its end date, carrying the hours it has left. A striped bar has
                      no end date in Autotask, so its finish is this page's guess at {capacity} hours a week; a red bar is
                      already past its end date, and its hours are owed this week.
                    </p>
                  </div>
                </div>
                <div className="card-body">
                  <div className="ps-gantt">
                    <div className="ps-row ps-head">
                      <div className="ps-row-label xs muted">
                        {num(bars.length)} project{bars.length === 1 ? '' : 's'}
                      </div>
                      <div className="ps-ticks">
                        {ticks.map((t) => (
                          <span key={t.w} className="ps-tick" style={{ left: `${(t.i / weeks.length) * 100}%` }}>
                            {weekLabel(t.w)}
                          </span>
                        ))}
                      </div>
                    </div>
                    {bars.map(({ pl, from, to, runsOn }) => {
                      const left = (from / weeks.length) * 100;
                      const width = ((to - from + 1) / weeks.length) * 100;
                      const cls = pl.late ? 'is-late' : pl.undated ? 'is-undated' : '';
                      return (
                        <div className="ps-row" key={pl.p.id}>
                          <div className="ps-row-label" title={`${pl.p.name}${pl.p.company ? ` · ${pl.p.company}` : ''}`}>
                            {pl.p.name}
                            <div className="xs muted">{[pl.p.company, pl.p.lead].filter(Boolean).join(' · ') || '—'}</div>
                          </div>
                          <div className="ps-track">
                            <div
                              className={`ps-bar ${cls}`}
                              style={{ left: `${left}%`, width: `${Math.max(width, 1.5)}%` }}
                              title={`${hrs(pl.p.remainingHours)} h left · ${pl.spans.length} week${pl.spans.length === 1 ? '' : 's'}${
                                pl.late ? ' · past its end date' : pl.undated ? ' · no end date' : ''
                              }`}
                            >
                              {hrs(pl.p.remainingHours)} h{runsOn ? ' →' : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {bars.length === 0 && <p className="muted small">Nothing lands in these {horizon} weeks.</p>}
                </div>
              </section>

              <section className="card">
                <div className="card-head">
                  <div className="grow">
                    <h3>The projects behind it</h3>
                    <p className="sub">
                      Hours left, who is on them, and the dates they are scheduled from.
                      {guessed > 0 &&
                        ` ${num(guessed)} of them ${guessed === 1 ? 'has no hours on its tasks' : 'have no hours on their tasks'}, so the project's own estimate less the hours worked is used instead.`}
                    </p>
                  </div>
                </div>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Lead</th>
                        <th>Status</th>
                        <th>Start</th>
                        <th>End</th>
                        <th className="num">Estimated</th>
                        <th className="num">Worked</th>
                        <th className="num">Left</th>
                        <th>On it</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bars.map(({ pl }) => (
                        <tr key={pl.p.id}>
                          <td>
                            {pl.p.name}
                            <div className="xs muted">{[pl.p.number, pl.p.company].filter(Boolean).join(' · ')}</div>
                          </td>
                          <td className="xs">{pl.p.lead ?? '—'}</td>
                          <td className="xs">{pl.p.status}</td>
                          <td className="xs" style={{ whiteSpace: 'nowrap' }}>{pl.p.start ? shortDate(pl.p.start) : '—'}</td>
                          <td className="xs" style={{ whiteSpace: 'nowrap' }}>
                            {pl.p.end ? shortDate(pl.p.end) : <span className="subtle">none</span>}
                            {pl.late && <div className="xs" style={{ color: 'var(--status-danger)' }}>past</div>}
                          </td>
                          <td className="num">{pl.p.estimatedHours === null ? '—' : hrs(pl.p.estimatedHours)}</td>
                          <td className="num">{pl.p.actualHours === null ? '—' : hrs(pl.p.actualHours)}</td>
                          <td className="num">
                            {hrs(pl.p.remainingHours)}
                            {pl.p.remainingFrom === 'estimate' && <div className="xs muted">from estimate</div>}
                          </td>
                          <td className="xs">
                            {pl.p.assignments.length === 0
                              ? <span className="subtle">nobody</span>
                              : pl.p.assignments.map((a) => `${a.resource} (${hrs(a.hours)})`).join(', ')}
                            {pl.p.unassignedHours > 0 && (
                              <div className="xs muted">{hrs(pl.p.unassignedHours)} h unassigned</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}
